
import React, { useEffect, useState } from "react";
import { isAdmin } from "@/utils/admin";
import { useAuth } from "@/providers/AuthProvider";
import AdminLayout from "@/components/AdminLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trophy, SearchIcon, UserPlus, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PermanentSubscription {
  id: string;
  user_id: string;
  email?: string;
  plan_name: string;
  status: string;
  created_at: string;
}

const AdminPermanentPlansPage: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [permanentPlans, setPermanentPlans] = useState<PermanentSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [foundUser, setFoundUser] = useState<{ id: string; email: string } | null>(null);
  const [searchingUser, setSearchingUser] = useState(false);
  const [assigningPlan, setAssigningPlan] = useState(false);
  
  useEffect(() => {
    if (user) {
      isAdmin(user.id).then(admin => {
        setIsAdminUser(admin);
        if (admin) {
          fetchPermanentPlans();
        }
      });
    }
  }, [user]);

  const fetchPermanentPlans = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select(`
        id,
        user_id,
        status,
        created_at,
        subscription_plan:subscription_plan_id (name)
      `)
      .eq('is_permanent', true);
    
    if (!error && data) {
      // Fetch user emails for the permanent plans
      const userIds = data.map(plan => plan.user_id);
      
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', userIds);
        
        const emailMap: Record<string, string> = {};
        if (profiles) {
          profiles.forEach(profile => {
            emailMap[profile.id] = profile.email;
          });
        }
        
        // Combine the data
        const plansWithEmail = data.map(plan => ({
          id: plan.id,
          user_id: plan.user_id,
          email: emailMap[plan.user_id] || 'Unknown',
          plan_name: plan.subscription_plan?.name || 'Unknown Plan',
          status: plan.status,
          created_at: plan.created_at
        }));
        
        setPermanentPlans(plansWithEmail);
      } else {
        setPermanentPlans([]);
      }
    }
    
    setLoading(false);
  };

  const searchUserByEmail = async () => {
    if (!searchEmail.trim()) return;
    
    setSearchingUser(true);
    setFoundUser(null);
    
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email')
      .ilike('email', searchEmail)
      .limit(1);
    
    if (error) {
      toast({
        title: "Error",
        description: "Failed to search for user",
        variant: "destructive",
      });
    } else if (data && data.length > 0) {
      setFoundUser(data[0]);
    } else {
      toast({
        title: "User not found",
        description: "No user found with that email address",
        variant: "destructive",
      });
    }
    
    setSearchingUser(false);
  };

  const assignPermanentPlan = async () => {
    if (!foundUser) return;
    
    setAssigningPlan(true);
    
    // First, get the Pro Plan ID
    const { data: plans, error: planError } = await supabase
      .from('subscription_plans')
      .select('id')
      .eq('name', 'Pro')
      .limit(1);
    
    if (planError || !plans || plans.length === 0) {
      toast({
        title: "Error",
        description: "Could not find Pro plan",
        variant: "destructive",
      });
      setAssigningPlan(false);
      return;
    }
    
    const proSubscriptionId = plans[0].id;
    
    // Check if user already has a subscription
    const { data: existingSubscription } = await supabase
      .from('user_subscriptions')
      .select('id')
      .eq('user_id', foundUser.id)
      .eq('status', 'active')
      .limit(1);
    
    let result;
    
    if (existingSubscription && existingSubscription.length > 0) {
      // Update existing subscription to permanent
      result = await supabase
        .from('user_subscriptions')
        .update({
          is_permanent: true,
          subscription_plan_id: proSubscriptionId,
          billing_period: 'yearly'  // Set to yearly for permanent plans
        })
        .eq('id', existingSubscription[0].id);
    } else {
      // Create new permanent subscription
      result = await supabase
        .from('user_subscriptions')
        .insert({
          user_id: foundUser.id,
          subscription_plan_id: proSubscriptionId,
          status: 'active',
          is_permanent: true,
          billing_period: 'yearly'  // Set to yearly for permanent plans
        });
    }
    
    if (result.error) {
      toast({
        title: "Error",
        description: "Failed to assign permanent plan",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: `Permanent Pro Plan assigned to ${foundUser.email}`,
      });
      
      // Refresh the list and clear search
      fetchPermanentPlans();
      setFoundUser(null);
      setSearchEmail("");
    }
    
    setAssigningPlan(false);
  };

  if (!user) {
    return (
      <AdminLayout title="Permanent Pro Access">
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
          <p className="text-gray-500">You must be logged in to access this page.</p>
        </div>
      </AdminLayout>
    );
  }

  if (!isAdminUser) {
    return (
      <AdminLayout title="Permanent Pro Access">
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access this page.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Permanent Pro Access">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="space-y-1">
              <CardTitle className="text-sm font-medium">
                Total Permanent Plans
              </CardTitle>
              <CardDescription>
                Users with lifetime access
              </CardDescription>
            </div>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? "..." : permanentPlans.length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Assign Permanent Pro Access
            </CardTitle>
            <CardDescription>
              Search for a user to assign lifetime Pro access
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Search user by email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                variant="outline"
                onClick={searchUserByEmail}
                disabled={searchingUser || !searchEmail.trim()}
              >
                {searchingUser ? "Searching..." : "Search"}
                <SearchIcon className="h-4 w-4 ml-2" />
              </Button>
            </div>
            
            {foundUser && (
              <div className="mt-4 p-3 border rounded-md">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{foundUser.email}</p>
                    <p className="text-xs text-muted-foreground">{foundUser.id}</p>
                  </div>
                  <Button
                    onClick={assignPermanentPlan}
                    disabled={assigningPlan}
                    className="gap-1"
                  >
                    <UserPlus className="h-4 w-4" />
                    {assigningPlan ? "Assigning..." : "Assign Permanent Pro"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Permanent Pro Users</CardTitle>
          <CardDescription>
            Users with lifetime Pro access
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center">Loading permanent plans...</div>
          ) : permanentPlans.length === 0 ? (
            <div className="py-10 text-center flex flex-col items-center gap-4">
              <AlertCircle className="h-12 w-12 text-muted-foreground" />
              <div>
                <p className="font-medium">No permanent plans found</p>
                <p className="text-sm text-muted-foreground">
                  Use the search above to assign permanent Pro access to a user
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User Email</TableHead>
                    <TableHead>User ID</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permanentPlans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell className="font-medium">{plan.email}</TableCell>
                      <TableCell className="font-mono text-xs">{plan.user_id}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800">
                          <Trophy className="h-3 w-3" />
                          Permanent {plan.plan_name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          plan.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {plan.status}
                        </span>
                      </TableCell>
                      <TableCell>{new Date(plan.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminPermanentPlansPage;
